import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
	getLessonsAdded,
	getLessonsStatus,
	getMapProjects,
	getMyOpenReportsCount,
	getMyProjectsCount,
	getMyTasksSummary,
	getReportsApproved,
	getReportStatus,
} from "../services/api-server/dashboard";
import { getProjects } from "../services/api-server/projects";
import {
	LessonFilters,
	LessonMonths,
	LessonsStatusPeriod,
	ProjectFilter,
	ReportPeriod,
	WidgetTarget,
} from "../types/dashboard.types";

/**
 * Query keys always carry the widget's tenant, scope and parameters, so no
 * cache entry is shared between tenants or views. Keys for the caller's own
 * data also carry their email. A null target leaves the query disabled.
 */
const DASHBOARD_QUERY_OPTIONS = {
	staleTime: 60_000,
	// Retry once, except client errors (400/403), which a retry cannot fix.
	retry: (failureCount: number, error: unknown) => {
		const status = isAxiosError(error) ? error.response?.status : undefined;
		return failureCount < 1 && !(status && status >= 400 && status < 500);
	},
	refetchOnWindowFocus: false,
} as const;

export const useReportStatus = (
	target: WidgetTarget | null,
	period: ReportPeriod,
	filters: ProjectFilter = {}
) =>
	useQuery({
		queryKey: [
			"dashboard",
			"report-status",
			target?.tenantKey,
			target?.scope,
			period,
			filters,
		],
		queryFn: () => getReportStatus(target!, period, filters),
		enabled: !!target,
		...DASHBOARD_QUERY_OPTIONS,
	});

export const useReportsApproved = (
	target: WidgetTarget | null,
	year: number,
	filters: ProjectFilter = {}
) =>
	useQuery({
		queryKey: [
			"dashboard",
			"reports-approved",
			target?.tenantKey,
			target?.scope,
			year,
			filters,
		],
		queryFn: () => getReportsApproved(target!, year, filters),
		enabled: !!target,
		...DASHBOARD_QUERY_OPTIONS,
	});

export const useLessonsAdded = (
	target: WidgetTarget | null,
	months: LessonMonths,
	filters: LessonFilters = {}
) =>
	useQuery({
		queryKey: [
			"dashboard",
			"lessons-added",
			target?.tenantKey,
			target?.scope,
			months,
			filters,
		],
		queryFn: () => getLessonsAdded(target!, months, filters),
		enabled: !!target,
		...DASHBOARD_QUERY_OPTIONS,
	});

export const useLessonsStatus = (
	target: WidgetTarget | null,
	period: LessonsStatusPeriod,
	filters: Omit<LessonFilters, "lessonType"> = {}
) =>
	useQuery({
		queryKey: [
			"dashboard",
			"lessons-status",
			target?.tenantKey,
			target?.scope,
			period,
			filters,
		],
		queryFn: () => getLessonsStatus(target!, period, filters),
		enabled: !!target,
		...DASHBOARD_QUERY_OPTIONS,
	});

/** The `my_tasks` prefix lets every existing My Tasks invalidation refresh it. */
export const useMyTasksSummary = (
	target: WidgetTarget | null,
	email: string | undefined
) =>
	useQuery({
		queryKey: [
			"my_tasks",
			"dashboard",
			target?.tenantKey,
			target?.scope,
			email,
		],
		queryFn: () => getMyTasksSummary(target!),
		enabled: !!target && !!email,
		...DASHBOARD_QUERY_OPTIONS,
	});

/** The `projects` prefix lets existing project invalidations refresh the map. */
export const useMapProjects = (tenantKey: string | null) =>
	useQuery({
		queryKey: ["projects", tenantKey, "dashboard-map"],
		queryFn: () => getMapProjects(tenantKey!),
		enabled: !!tenantKey,
		...DASHBOARD_QUERY_OPTIONS,
	});

export const useMyProjectsCount = (
	urlTenantKey: string | null,
	tenantKeys: string[],
	email: string | undefined
) => {
	const sortedKeys = [...tenantKeys].sort();
	return useQuery({
		queryKey: ["dashboard", "my-projects", sortedKeys, email],
		queryFn: () => getMyProjectsCount(urlTenantKey!, sortedKeys),
		enabled: !!urlTenantKey && !!email && sortedKeys.length > 0,
		...DASHBOARD_QUERY_OPTIONS,
	});
};

/** The `my_reports` prefix lets report saves, which refresh My reports, refresh it too. */
export const useMyOpenReportsCount = (
	tenantKey: string | null,
	email: string | undefined
) =>
	useQuery({
		queryKey: ["my_reports", "dashboard", tenantKey, email],
		queryFn: () => getMyOpenReportsCount(tenantKey!),
		enabled: !!tenantKey && !!email,
		...DASHBOARD_QUERY_OPTIONS,
	});

/** Non-removed projects of one tenant, as filter options. */
export const useProjectOptions = (tenantKey: string | null) =>
	useQuery({
		queryKey: ["projects", tenantKey, "dashboard-filter"],
		queryFn: async () => ((await getProjects(tenantKey)) as any[] | null) ?? [],
		select: (projects) =>
			projects.map((project) => ({
				value: String(project._id),
				label: String(project.project_name),
			})),
		enabled: !!tenantKey,
		...DASHBOARD_QUERY_OPTIONS,
	});
